import { notificationService } from '@/services/notification.service';
import { useNotificationsStore } from '@/store/notifications.store';
import { Notification, UpdateReadStatusDto } from '@/types/notification.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (filters: object, pagination: object) =>
    ['notifications', 'list', { filters, pagination }] as const,
  unreadCount: () => ['notifications', 'unread-count'] as const,
};

// Hook pour récupérer les notifications avec pagination et filtres
export const useNotifications = () => {
  const {
    notifications,
    getFilters,
    getPagination,
    setNotifications,
    setPagination,
    setLoading,
    setError,
  } = useNotificationsStore();

  const filters = getFilters();
  const pagination = getPagination();

  const query = useQuery({
    queryKey: notificationKeys.list(filters, pagination),
    queryFn: async () => {
      const response = await notificationService.getNotifications(
        pagination,
        filters
      );
      return response;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });

  // Synchroniser les données avec le store
  useEffect(() => {
    if (query.data) {
      setNotifications(query.data.data);
      setPagination(query.data.totalCount, query.data.totalPages);
    }
    setLoading(query.isLoading);
    setError(query.error?.message || null);
  }, [
    query.data,
    query.isLoading,
    query.error,
    setNotifications,
    setPagination,
    setLoading,
    setError,
  ]);

  return {
    notifications,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 0,
    unreadCount: query.data?.unreadCount || 0,
  };
};

// Hook pour le compteur de notifications non lues
export const useUnreadCount = () => {
  const { setUnreadCount } = useNotificationsStore();

  const query = useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const response = await notificationService.getUnreadCount();
      return response;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes
    refetchOnWindowFocus: true,
  });

  // Synchroniser avec le store
  useEffect(() => {
    if (query.data?.data?.unreadCount !== undefined) {
      setUnreadCount(query.data.data.unreadCount);
    }
  }, [query.data?.data?.unreadCount, setUnreadCount]);

  return {
    unreadCount: query.data?.data?.unreadCount || 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

// Hook pour marquer une notification comme lue/non lue
export const useUpdateReadStatus = () => {
  const queryClient = useQueryClient();
  const { updateNotification } = useNotificationsStore();

  return useMutation({
    mutationFn: async ({
      notificationId,
      data,
    }: {
      notificationId: string;
      data: UpdateReadStatusDto;
    }) => {
      await notificationService.updateReadStatus(notificationId, data);
      return { notificationId, data };
    },
    onMutate: async ({ notificationId, data }) => {
      // Optimistic update
      const { notifications } = useNotificationsStore.getState();
      const notification = notifications.find(n => n.uuid === notificationId);

      if (notification) {
        const updatedNotification: Notification = {
          ...notification,
          isRead: data.isRead,
        };
        updateNotification(updatedNotification);
      }

      return { notificationId, previousValue: notification?.isRead };
    },
    onError: (_error, { notificationId }, context) => {
      // Rollback optimistic update
      if (context?.previousValue !== undefined) {
        const { notifications } = useNotificationsStore.getState();
        const notification = notifications.find(n => n.uuid === notificationId);

        if (notification) {
          const revertedNotification: Notification = {
            ...notification,
            isRead: context.previousValue,
          };
          updateNotification(revertedNotification);
        }
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

// Hook pour marquer toutes les notifications comme lues
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();
  const { notifications, setNotifications } = useNotificationsStore();

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onMutate: async () => {
      // Optimistic update - mark all as read
      const updatedNotifications = notifications.map(notification => ({
        ...notification,
        isRead: true,
      }));
      setNotifications(updatedNotifications);

      return { previousNotifications: notifications };
    },
    onError: (_error, _variables, context) => {
      // Rollback optimistic update
      if (context?.previousNotifications) {
        setNotifications(context.previousNotifications);
      }
    },
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });
    },
  });
};

// Hook pour supprimer une notification
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  const { removeNotification, notifications } = useNotificationsStore();

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.deleteNotification(notificationId),
    onMutate: async notificationId => {
      // Optimistic update
      const notification = notifications.find(n => n.uuid === notificationId);
      removeNotification(notificationId);

      return { deletedNotification: notification };
    },
    onError: () => {
      // We can't easily rollback a removal, so we'll just refetch
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onSuccess: () => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });
    },
  });
};

// Hook pour la pagination des notifications
export const useNotificationsPagination = () => {
  const {
    currentPage,
    totalPages,
    totalCount,
    limit,
    nextPage,
    prevPage,
    goToPage,
    setCurrentPage,
  } = useNotificationsStore();

  const hasNext = currentPage < totalPages;
  const hasPrev = currentPage > 1;

  return {
    currentPage,
    totalPages,
    totalCount,
    limit,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    goToPage,
    setCurrentPage,
  };
};

// Hook pour marquer automatiquement comme lu lors de l'affichage
export const useMarkAsReadOnView = (
  notificationId: string,
  isVisible: boolean = true
) => {
  const { mutate: updateReadStatus } = useUpdateReadStatus();
  const { notifications } = useNotificationsStore();

  const markAsRead = useCallback(() => {
    const notification = notifications.find(n => n.uuid === notificationId);
    if (notification && !notification.isRead && isVisible) {
      updateReadStatus({ notificationId, data: { isRead: true } });
    }
  }, [notificationId, notifications, updateReadStatus, isVisible]);

  return markAsRead;
};
